/**
 * @file rate.constants.js
 * @description Helper constants for rate configuration and UI tables.
 */

/**
 * Available hour selection options (in hours).
 * @type {number[]}
 */
export const SELECT_HOURS = [1, 5, 10, 15, 24, 36, 48, 72, 96, 120];

/**
 * Minimum selection values used in forms (integers).
 * @type {number[]}
 */
export const MIN_SELECT_OPTIONS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

/**
 * Time unit options for dropdowns.
 * @type {{value: string, label: string}[]}
 */
export const TIMES = [
    { value: "MINUTES", label: "Minutes" },
    { value: "HOURS", label: "Hours" },
    { value: "DAYS", label: "Days" }
];

/**
 * Cancellation policy options to present in the UI.
 * @type {{value: string, label: string}[]}
 */
export const CANCELLATION_OPTIONS = [
    { value: "FULL_REFUND", label: "Full Refund" },
    { value: "PARTIAL_REFUND", label: "Partial Refund" },
    { value: "CHANGES", label: "Changes" }
];

/**
 * Columns configuration for Services Areas table.
 * Used by table components to render headers and styles.
 * @type {Object[]}
 */
export const FIELDS_SERVICES_AREAS = [
    {
        key: "areaCode",
        label: "Area Code",
        sortable: true,
        thStyle: { minWidth: "160px", paddingLeft: "15px !important" },
        class: "text-left pl-3"
    },
    {
        key: "description",
        label: "Area Description",
        sortable: true,
        thStyle: { minWidth: "200px" },
        class: "text-left"
    },
    {
        key: "baseprice",
        label: "Base Price",
        sortable: true,
        thStyle: { minWidth: "160px" },
        class: "text-right"
    },
    {
        key: "priceextra",
        label: "Price Extra",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-right"
    },
    {
        key: "actions",
        label: "Actions",
        sortable: false,
        thStyle: { minWidth: "100px" },
        class: "alinmov6"
    }
];

/**
 * Columns configuration for Surcharges table.
 * @type {Object[]}
 */
export const FIELDS_SURCHARGES = [
    {
        key: "event",
        label: "Event",
        sortable: true,
        thStyle: { minWidth: "160px", paddingLeft: "15px !important" },
        class: "text-left pl-3"
    },
    {
        key: "description",
        label: "Description",
        sortable: true,
        thStyle: { minWidth: "200px" },
        class: "text-left"
    },
    {
        key: "markupType",
        label: "Surcharge",
        sortable: true,
        thStyle: { minWidth: "160px" },
        class: "text-left"
    },
    {
        key: "value",
        label: "Value",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-left"
    },
    {
        key: "from",
        label: "From",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-left"
    },
    {
        key: "end",
        label: "End",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-left"
    },
    {
        key: "status",
        label: "Status",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-center"
    },
    {
        key: "actions",
        label: "Actions",
        sortable: false,
        thStyle: { minWidth: "100px" },
        class: "alinmov6"
    }
];

/**
 * Generic fields configuration for rates / vehicles table.
 * @type {Object[]}
 */
export const FIELDS_RATES = [
    {
        key: "selected",
        label: "",
        sortable: false,
        class: "text-center",
        thStyle: { minWidth: "40px" }
    },
    {
        key: "category",
        label: "Category",
        sortable: true,
        thStyle: { minWidth: "200px" },
        class: "text-left"
    },
    {
        key: "type",
        label: "Vehicle Type",
        sortable: true,
        thStyle: { minWidth: "110px" },
        class: "alinmov6"
    },
    {
        key: "model",
        label: "Make/Model",
        sortable: true,
        thStyle: { minWidth: "150px" },
        class: "text-left"
    },
    {
        key: "seats",
        label: "Seats",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-right"
    },
    {
        key: "bags",
        label: "Bags",
        sortable: true,
        thStyle: { minWidth: "100px" },
        class: "text-right pr-3"
    },
    {
        key: "tags",
        label: "Tags",
        sortable: true,
        thStyle: { minWidth: "80px", paddingRight: "15px !important" },
        class: "text-left"
    }
];

/**
 * Supported currencies and symbols.
 * @type {{code: string, symbol: string, label: string}[]}
 */
export const COINS = [
    { code: "USD", symbol: "$", label: "US Dollar" },
    { code: "EUR", symbol: "€", label: "Euro" },
    { code: "GBP", symbol: "£", label: "British Pound" },
    { code: "CHF", symbol: "₣", label: "Swiss Franc" },
    { code: "DKK", symbol: "kr", label: "Denmark Krone" },
    { code: "SEK", symbol: "kr", label: "Swedish Krona" },
    { code: "NOK", symbol: "kr", label: "Norwegian Krone" },
    { code: "ISK", symbol: "kr", label: "Icelandic Króna" },
    { code: "AED", symbol: "د.إ", label: "United Arab Emirates Dirham" }
];

/**
 * Default terms and conditions string used in booking pages.
 * @type {string}
 */
export const TERMS_AND_CONDITIONS =
    "Arrivals by Plane: Our chauffeur will meet you in the arrivals lounge after luggage pickup, holding a sign with the lead passenger’s name. We offer 60 minutes of free waiting time. If you don’t arrive or notify us within this period, the transfer will be marked as a NO SHOW and fully charged. For lost luggage or customs delays, please inform us before the waiting time expires. If you miss a connecting flight, notify us promptly to reschedule. Flight cancellations result in automatic free transfer cancellation. Arrivals by Train or Cruise: Our chauffeur will wait at a designated meeting point near your arrival platform, holding a sign with the lead passenger’s name. Departures: Pick-up is from your specified location. The free waiting period is 20 minutes.";



/**
* Cancellation policies for small vehicles.
* Each policy defines the refund type, time unit, cutoff time, and refund percentage.
* Used to calculate refunds based on cancellation timing and vehicle type.
* @type {Object[]}
*/
export const CANCELLATION_POLICY_SMALL_VEHICLES = [
    {
        type: "FULL_REFUND",
        unit: "DAYS",
        cutoff: 1,
        percent: 100
    },
    {
        type: "PARTIAL_REFUND",
        unit: "HOURS",
        cutoff: 12,
        percent: 50
    },
    {
        type: "NO_REFUND",
        unit: "HOURS",
        cutoff: 12,
        percent: 0
    }
];

/**
* Cancellation policies for big vehicles.
* Each policy defines the refund type, time unit, cutoff time, and refund percentage.
* Used to calculate refunds based on cancellation timing and vehicle type.
* @type {Object[]}
*/
export const CANCELLATION_POLICY_BIG_VEHICLES = [
    {
        type: "FULL_REFUND",
        unit: "DAYS",
        cutoff: 2,
        percent: 100
    },
    {
        type: "PARTIAL_REFUND",
        unit: "DAYS",
        cutoff: 1,
        percent: 50
    },
    {
        type: "NO_REFUND",
        unit: "DAYS",
        cutoff: 1,
        percent: 0
    }
];